import * as React from "react"
import { Link } from "gatsby"

import CodeBlock from "../../components/CodeBlock"
import Layout from "../../components/layout"
import Seo from "../../components/seo"

const AREAS = [
  ["Eligibility gates", "the hard requirements Google checks before anything else"],
  ["Policy & security", "program-policy compliance, remote code, data handling"],
  ["Technical best practices", "Manifest V3, bfcache, permission minimization"],
  ["Privacy", "privacy policy, disclosures, least-privilege data access"],
  ["UX quality", "onboarding, polish, and the judgment calls a human reviewer makes"],
  ["Store listing quality", "description, screenshots, promo assets"],
  ["Publisher prerequisites", "verified publisher status and account standing"],
]

const CwsFeaturedBadgePage = ({ location }) => {
  return (
    <Layout location={location} title="JavaScriptBit">
      <article>
        <header>
          <h1>Chrome Web Store Featured Badge Audit</h1>
          <p style={{ fontSize: "var(--fontSize-2, 1.2rem)", color: "var(--color-text-light, #4f5969)" }}>
            A Claude Code plugin that audits your Chrome extension against
            Google's published criteria for the <strong>Featured badge</strong>{" "}
            and <strong>Established Publisher badge</strong>, then produces a
            prioritized fix plan to qualify.
          </p>
        </header>

        <p>
          The Featured badge is a human review by the Chrome Web Store team —
          there's no queue you can pay or fast-track your way through. The only
          lever is genuinely meeting the published bar. This plugin tells you
          exactly where you stand and what to fix first, before you nominate.
        </p>

        <h2>Install</h2>
        <p>In Claude Code:</p>
        <CodeBlock code="/plugin marketplace add divyamrastogi/cws-featured-badge-skill" />
        <CodeBlock code="/plugin install cws-featured-badge@divyamrastogi-skills" />
        <p>
          Then open Claude Code in your extension's repo and ask:{" "}
          <em>"Is my extension ready for the Featured badge?"</em>
        </p>

        <h2>What it checks</h2>
        <p>
          The audit gathers evidence from your codebase (manifest, permissions,
          privacy behavior, onboarding, store assets) and your live listing,
          covering ~35 items across seven areas:
        </p>
        <ul>
          {AREAS.map(([name, detail]) => (
            <li key={name}>
              <strong>{name}</strong> — {detail}
            </li>
          ))}
        </ul>
        <p>
          The output is a scorecard with evidence per item, blockers separated
          from high-impact improvements, the dashboard items you must verify
          manually, and a nomination-readiness verdict.
        </p>

        <p>
          Badges can't be bought. The plugin never suggests gaming tactics —
          incentivized reviews and keyword stuffing violate program policies
          and disqualify extensions. It only helps you genuinely meet the bar.
        </p>

        <h2>Links</h2>
        <ul>
          <li>
            <a href="https://github.com/divyamrastogi/cws-featured-badge-skill">
              Source on GitHub
            </a>{" "}
            (MIT licensed)
          </li>
          <li>
            <Link to="/cws-featured-badge/privacy/">Privacy policy</Link>
          </li>
          <li>
            <a href="https://support.google.com/chrome_webstore/answer/1050673">
              Google's badge criteria
            </a>{" "}
            — the source material the audit is built from
          </li>
        </ul>
      </article>
    </Layout>
  )
}

export default CwsFeaturedBadgePage

export const Head = () => (
  <Seo
    title="Chrome Web Store Featured Badge Audit — Claude Code plugin"
    description="Audit your Chrome extension against Google's Featured badge and Established Publisher badge criteria, with a prioritized fix plan to qualify."
  />
)
